// File: utils/WebNativeBridge.js

/**
 * Simplified utility to handle user ID injection from React Native WebView
 */
class WebNativeBridge {
    constructor() {
      this.listeners = {};
      this.isInitialized = false;
      this.lastInjectedUserId = null;
      
      // Check if we're running in React Native WebView
      this.isInReactNative = !!window.ReactNativeWebView;
      
      // // Log initialization
      // console.log('WebNativeBridge created');
      // console.log('Running in React Native WebView:', this.isInReactNative);
    }
  
    /**
     * Initialize the bridge and set up message listeners
     */
    init() {
      if (this.isInitialized) {
        // console.log('WebNativeBridge already initialized');
        return;
      }
  
      // console.log('Initializing WebNativeBridge...');
      
      // Check if window.receiveData is already defined
      if (window.receiveData) {
        console.warn('window.receiveData is already defined, it will be overwritten');
      }
  
      // Set a global function that React Native can call to inject data
      window.receiveData = this.receiveData.bind(this);
      
      // For debugging, also listen to message events from React Native
      window.addEventListener('message', (event) => {
        // console.log('Received message event:', event.data);
        try {
          // Try to parse it as JSON
          const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          //console.log('Parsed message data:', data);
          
          // If it has a userId field, treat it as a user ID
          if (data && data.userId) {
            this.receiveData(data.userId);
          }
        } catch (e) {
          // If it's not JSON, it might be a direct ID
          if (typeof event.data === 'string') {
            this.receiveData(event.data);
          }
        }
      });
      
      this.isInitialized = true;
      // console.log('WebNativeBridge initialized, window.receiveData is now defined');
      
      // For debugging - log if initialized but userId already injected
      if (this.lastInjectedUserId) {
        console.log('User ID was already injected before initialization:', this.lastInjectedUserId);
      }
    }
  
    /**
     * Callback that React Native calls directly to inject user ID
     */
    receiveData(userId) {
      try {
        // console.log('🔔 Received user ID from React Native:', userId);
        this.lastInjectedUserId = userId;
        
        // Notify all USER_ID listeners of the received ID
        this.notify('USER_ID', userId);
        
        // Show in console as a highlighted message
        // console.log('%c User ID Received: ' + userId, 'background: #4CAF50; color: white; padding: 2px 5px; border-radius: 3px;');
        
        return true;
      } catch (error) {
        console.error('Error receiving user ID from React Native:', error);
        return false;
      }
    }
  
    /**
     * Send a message to React Native (if needed)
     */
    sendToNative(message) {
      try {
        // Check if we're in a WebView context with a postMessage method
        if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
          console.log('Sending message to React Native:', message);
          window.ReactNativeWebView.postMessage(JSON.stringify(message));
        } else {
          console.warn('ReactNativeWebView not available. Running in browser mode.');
        }
      } catch (error) {
        console.error('Error sending message to React Native:', error);
      }
    }
  
    /**
     * Register a listener for a specific message type
     */
    on(type, callback) {
      if (!this.listeners[type]) {
        this.listeners[type] = [];
      }
      
      this.listeners[type].push(callback);
      
      // If this is a USER_ID listener and we already have a user ID, immediately notify
      if (type === 'USER_ID' && this.lastInjectedUserId !== null) {
       // console.log('Immediately notifying new listener of existing user ID:', this.lastInjectedUserId);
        callback(this.lastInjectedUserId);
      }
      
      return () => this.off(type, callback);
    }
  
    /**
     * Remove a listener
     */
    off(type, callback) {
      if (!this.listeners[type]) return;
      
      this.listeners[type] = this.listeners[type].filter(cb => cb !== callback);
    }
  
    /**
     * Notify all listeners of a particular message type
     */
    notify(type, payload) {
      if (!this.listeners[type]) {
        //console.log(`No listeners registered for ${type}`);
        return;
      }
      
      // console.log(`Notifying ${this.listeners[type].length} listeners of type ${type}`);
      
      this.listeners[type].forEach(callback => {
        try {
          callback(payload);
        } catch (error) {
          console.error(`Error in listener for ${type}:`, error);
        }
      });
    }
  
    /**
     * Get the last injected user ID
     */
    getLastInjectedUserId() {
      return this.lastInjectedUserId;
    }
  }
  
  // Create a singleton instance
  const webNativeBridge = new WebNativeBridge();
  export default webNativeBridge;
  
